import { useAtomValue } from 'jotai';
import { RefObject, TouchEvent, useRef, useState } from 'react';

import { isCtaModalOpenAtom } from '../../store/index.atom';
import { v2ZoomAtom } from '../../store/v2/viewer.atom';

/* 줌 상태 엣지 오버스크롤 스와이프 임계값(px). 오조작 방지를 위해 넉넉히 잡는다 */
const EDGE_SWIPE_THRESHOLD = 60;
const EDGE_TOLERANCE = 1;

interface Props {
  totalPage: number;
  isNextSwipeDisabled?: boolean;
  onPageChange: (page: number, currentPage: number) => void;
  currentPage: number;
  /** 줌 상태 엣지 판별용 네이티브 스크롤 컨테이너 */
  scrollRef?: RefObject<HTMLElement>;
}

/**
 * V2 페이지 모드 스와이프 내비게이션.
 * - 기본(줌 100% 이하): V1 useSwipe와 동일한 ±10px 임계값.
 * - 줌 상태(>100%): 콘텐츠를 가로 끝까지 스크롤한 뒤 같은 방향으로
 *   한 번 더 크게(±60px) 스와이프하면 페이지를 넘긴다(엣지 오버스크롤).
 */
const useV2Swipe = ({
  totalPage,
  isNextSwipeDisabled,
  onPageChange,
  currentPage,
  scrollRef,
}: Props) => {
  const zoom = useAtomValue(v2ZoomAtom);
  const isCtaModalOpen = useAtomValue(isCtaModalOpenAtom);

  const touchStartRef = useRef(0);
  const edgeStateRef = useRef({ atLeftEdge: false, atRightEdge: false });
  /*
   * 핀치 세션 플래그. 두 손가락이 닿은 순간부터 마지막 손가락이 떨어질 때까지
   * 스와이프 판정을 전부 무시한다. 2→1 손가락 전환 후 남은 손가락의 이동이
   * 스와이프로 오인돼 페이지가 넘어가는 것을 막는다.
   */
  const isPinchSessionRef = useRef(false);
  const [value, setValue] = useState<'prev' | 'next' | null>(null);

  const getEdgeState = () => {
    const scrollEl = scrollRef?.current;
    if (!scrollEl) {
      return { atLeftEdge: false, atRightEdge: false };
    }
    return {
      atLeftEdge: scrollEl.scrollLeft <= EDGE_TOLERANCE,
      atRightEdge:
        scrollEl.scrollLeft >=
        scrollEl.scrollWidth - scrollEl.clientWidth - EDGE_TOLERANCE,
    };
  };

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    if (event.touches.length > 1) {
      isPinchSessionRef.current = true;
      setValue(null);
      return;
    }

    touchStartRef.current = event.touches[0].clientX;
    /* 제스처 시작 시점에 이미 엣지에 있어야 오버스크롤로 인정한다 */
    edgeStateRef.current = getEdgeState();
  };

  const handleTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    /* 핀치 전환 시 첫 손가락이 래치한 스와이프를 무효화한다 */
    if (event.touches.length > 1) {
      isPinchSessionRef.current = true;
      setValue(null);
      return;
    }

    if (isPinchSessionRef.current) {
      return;
    }

    if (isCtaModalOpen) {
      return;
    }

    const touchDiff = event.touches[0].clientX - touchStartRef.current;

    if (zoom > 1) {
      /* 줌 상태: 시작·현재 모두 엣지일 때만 큰 스와이프로 페이지 이동 */
      const currentEdge = getEdgeState();

      if (
        touchDiff > EDGE_SWIPE_THRESHOLD &&
        edgeStateRef.current.atLeftEdge &&
        currentEdge.atLeftEdge
      ) {
        setValue('prev');
      } else if (
        touchDiff < -EDGE_SWIPE_THRESHOLD &&
        edgeStateRef.current.atRightEdge &&
        currentEdge.atRightEdge
      ) {
        setValue('next');
      } else {
        setValue(null);
      }
      return;
    }

    if (touchDiff > 10) {
      setValue('prev');
    } else if (touchDiff < -10) {
      setValue('next');
    }
  };

  const handleTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    if (isPinchSessionRef.current) {
      /* 마지막 손가락이 떨어져야 핀치 세션이 끝난다 */
      if (event.touches.length === 0) {
        isPinchSessionRef.current = false;
      }
      setValue(null);
      return;
    }

    if (!value || isCtaModalOpen) {
      return;
    }

    if (value === 'next') {
      setValue(null);
      if (currentPage === totalPage || isNextSwipeDisabled) {
        return;
      }
      onPageChange(currentPage + 1, currentPage);
    } else if (value === 'prev') {
      setValue(null);
      if (currentPage === 1) {
        return;
      }
      onPageChange(currentPage - 1, currentPage);
    }
  };

  return {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  };
};

export default useV2Swipe;
